import { Controller } from '@hotwired/stimulus'
import { lastLoadedItem, PRODUCT_PREFIX } from '../utils/grid_items'

// This controller allows to throttle a Turbo frame based
// pagination system. The frame used to trigger the next
// page load is initially disabled. When the system is ready,
// in particular when masonry has finished its rendering magic,
// it can be re-enabled.
export default class extends Controller {
  static targets = ['frame', 'items', 'scope']

  static values = {
    bottomMargin: { type: Number, default: 1000 },
  }

  connect() {
    this.isLoading = false
    this.ready = false
  }

  // Le flux de pagination remplace la frame à chaque page, et elle arrive désactivée.
  // L'ordre des deux actions du flux (ajout des cartes, remplacement de la frame) n'est
  // pas garanti : le signal d'activation peut partir avant que la nouvelle frame soit
  // là. On la réactive donc aussi à sa connexion, une fois la grille prête.
  frameTargetConnected(frame) {
    if (!this.ready) return

    // On réactive seulement : déclencher le chargement ici aussi ferait partir deux
    // requêtes pour la même position (page ajoutée en double). Le prochain événement
    // de défilement, ou le signal de la grille, s'en charge.
    this.isLoading = false
    frame.disabled = Boolean(this.paused)
  }

  enableBeaconTargetConnected(beaconElement) {
    this.enable()
    beaconElement.remove()
  }

  enable() {
    this.ready = true
    this.isLoading = false

    if (this.hasFrameTarget) {
      this.frameTarget.disabled = false
      this.load()
    }
  }

  // Glisser d'une section en mode Organiser : les cartes sont repliées sous leurs titres
  // (sortable#foldSections), et ce qu'une page apporterait n'appartiendrait à aucun bloc replié —
  // au lâcher, ses envies se rangeraient sous le dernier titre posé (#462). Pas de page tant que
  // dure ce glisser ; celui d'une carte, lui, en charge : on peut l'emmener plus bas.
  // La frame est désactivée, pas seulement ignorée : chargée au besoin (loading: lazy), Turbo la
  // chargerait de lui-même en la voyant entrer à l'écran, ce que le repli provoque justement.
  pause() {
    this.paused = true
    if (this.hasFrameTarget) this.frameTarget.disabled = true
  }

  // Sans charger tout de suite : le nouvel ordre part au lâcher (sortable#reorder), et le curseur
  // lu avant qu'il soit enregistré partirait de la place d'avant. Le défilement suivant s'en charge.
  resume() {
    if (!this.paused) return

    this.paused = false
    if (this.ready && this.hasFrameTarget) this.frameTarget.disabled = false
  }

  load() {
    if (this.paused || this.isLoading || !this.hasFrameTarget) return

    if (this.#mustTrigger()) {
      this.isLoading = true
      this.frameTarget.loading = 'eager'
    }
  }

  // Grille d'une liste : la page suivante part du dernier produit affiché, lu au moment où la
  // requête part — et non d'un numéro de page, que les pages chargées entre-temps démentent dès
  // qu'une envie est retirée ou ajoutée au-dessus (cf. Lists::ProductsLoader#after_product).
  // Toute requête partie de la grille passe par ici (suppression, favori, section…) : sans frame,
  // liste courte ou dernière page chargée, il n'y a rien à faire.
  stampCursor(event) {
    if (!this.hasFrameTarget || event.target !== this.frameTarget) return

    const last = lastLoadedItem(this.itemsTarget, { productsOnly: true })
    if (last) event.detail.url.searchParams.set('after', last.id.slice(PRODUCT_PREFIX.length))
  }

  #mustTrigger() {
    // Grille masquée (celle de la fiche produit, réservée au desktop) : sa hauteur nulle
    // la disait toujours arrivée en bas, et elle chargeait toutes ses pages en fond.
    if (this.itemsTarget.offsetParent === null) return false

    let scopeBottom
    const itemsBottom = this.itemsTarget.offsetTop + this.itemsTarget.offsetHeight

    if (this.hasScopeTarget) {
      scopeBottom = this.scopeTarget.scrollTop + this.scopeTarget.offsetHeight
    } else {
      scopeBottom = window.scrollY + window.innerHeight
    }

    return scopeBottom > itemsBottom - this.bottomMarginValue
  }
}
